/**
 * Client analytics — Wave 3 sampled observability.
 *
 * Before: every page nav → userAnalytics update + systemLogs user_action + time_spent
 *          (~3–5 Firestore writes per navigation).
 * After:  ordinary page views/actions are local/sampled; time_spent never writes;
 *         Firestore reserved for sampled sessions + errors/conversions/security.
 */
import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import systemLogger from './systemLogger';

const SAMPLE = {
  sessionStart: 0.05, // 5% of sessions touch userAnalytics
  pageView: 0, // never — was the hottest write path
  action: 0.01, // 1% of named actions
  feature: 0.02,
  timeSpent: 0, // killed
  sessionEnd: 0.05,
};

const alwaysPersistActions = new Set(['conversion', 'error']);

class AnalyticsService {
  constructor() {
    this.sessionStartTime = Date.now();
    this.pageStartTime = Date.now();
    this.currentPage =
      typeof window !== 'undefined' ? window.location.pathname : '/';
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.isInitialized = false;
    this.sessionSampled = Math.random() < SAMPLE.sessionStart;
    this._visibilityBound = false;
    this._unloadBound = false;
  }

  generateSessionId() {
    return `analytics_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  shouldSample(rate) {
    if (rate <= 0) return false;
    if (rate >= 1) return true;
    return Math.random() < rate;
  }

  async init(userId) {
    if (this.isInitialized && this.userId === userId) return;

    this.userId = userId;
    this.isInitialized = true;
    this.sessionSampled = Math.random() < SAMPLE.sessionStart;

    try {
      if (this.sessionSampled) {
        await this.trackSessionStart();
      }

      this.setupVisibilityTracking();
      this.setupBeforeUnloadTracking();
      // Initial page view is local-only (SAMPLE.pageView === 0)
      this.currentPage =
        typeof window !== 'undefined' ? window.location.pathname : '/';
      this.pageStartTime = Date.now();
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }

  async trackSessionStart() {
    if (!this.userId || !this.sessionSampled) return;

    const sessionData = {
      timestamp: Timestamp.now(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      location: this.getTimezoneBasedLocation(),
      deviceType: this.getDeviceType(),
      sessionDuration: 0,
    };

    try {
      const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);
      const userAnalyticsDoc = await getDoc(userAnalyticsRef);

      if (userAnalyticsDoc.exists()) {
        // Keep loginHistory capped — arrayUnion unbounded growth was expensive
        const prev = userAnalyticsDoc.data()?.loginHistory || [];
        const loginHistory = [...prev.slice(-19), sessionData];
        await updateDoc(userAnalyticsRef, {
          loginHistory,
          'engagementMetrics.totalSessions': increment(1),
          'engagementMetrics.lastActive': Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      } else {
        await setDoc(userAnalyticsRef, {
          userId: this.userId,
          loginHistory: [sessionData],
          actionHistory: [],
          engagementMetrics: {
            totalSessions: 1,
            totalTimeSpent: 0,
            pagesVisited: 0,
            featuresUsed: [],
            lastActive: Timestamp.now(),
            averageSessionTime: 0,
          },
          riskScore: 0,
          riskFactors: [],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Failed to track session start:', error);
    }
  }

  async trackAction(action, context = {}) {
    if (!this.userId) return;

    const persist =
      alwaysPersistActions.has(action) || this.shouldSample(SAMPLE.action);

    // Local / console only for ordinary actions
    if (!persist) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[analytics:local]', action, context);
      }
      return;
    }

    try {
      const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);
      const snap = await getDoc(userAnalyticsRef);
      if (!snap.exists()) {
        await this.createAnalyticsDocument();
      }

      const actionData = {
        timestamp: Timestamp.now(),
        action,
        context,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        duration: Date.now() - this.pageStartTime,
      };

      const prev = snap.exists() ? snap.data()?.actionHistory || [] : [];
      const actionHistory = [...prev.slice(-49), actionData];

      await updateDoc(userAnalyticsRef, {
        actionHistory,
        'engagementMetrics.lastActive': Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Failed to track action:', error);
    }
  }

  async trackPageView(page) {
    if (!this.userId) return;

    // Time-spent writes killed — keep local timers for session-end sampling only
    this.currentPage = page;
    this.pageStartTime = Date.now();

    if (!this.shouldSample(SAMPLE.pageView)) {
      return;
    }

    try {
      const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);
      await updateDoc(userAnalyticsRef, {
        'engagementMetrics.pagesVisited': increment(1),
        'engagementMetrics.lastActive': Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      // Doc may not exist for non-sampled sessions — ignore
      if (process.env.NODE_ENV === 'development') {
        console.debug('page view sample skipped:', error?.message);
      }
    }
  }

  async trackFeatureUsage(feature, metadata = {}) {
    if (!this.userId) return;
    if (!this.shouldSample(SAMPLE.feature)) {
      return this.trackAction('feature_usage', { feature, ...metadata });
    }

    try {
      const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);
      const snap = await getDoc(userAnalyticsRef);
      if (!snap.exists()) await this.createAnalyticsDocument();

      const prev = snap.exists() ? snap.data()?.engagementMetrics?.featuresUsed || [] : [];
      const featuresUsed = [...new Set([...prev, feature])].slice(-40);

      await updateDoc(userAnalyticsRef, {
        'engagementMetrics.featuresUsed': featuresUsed,
        'engagementMetrics.lastActive': Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      await this.trackAction('feature_usage', { feature, ...metadata });
    } catch (error) {
      console.error('Failed to track feature usage:', error);
    }
  }

  async trackTimeSpent(_category, _duration) {
    // Wave 3: killed — was 1–2 writes per navigation via systemLogs + userAnalytics
    return;
  }

  async createAnalyticsDocument() {
    if (!this.userId) return;

    const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);

    await setDoc(
      userAnalyticsRef,
      {
        userId: this.userId,
        loginHistory: [],
        actionHistory: [],
        engagementMetrics: {
          totalSessions: 0,
          totalTimeSpent: 0,
          pagesVisited: 0,
          featuresUsed: [],
          lastActive: Timestamp.now(),
          averageSessionTime: 0,
        },
        riskScore: 0,
        riskFactors: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  }

  async trackConversion(event, value = null, metadata = {}) {
    if (!this.userId) return;
    await this.trackAction('conversion', {
      event,
      value,
      ...metadata,
    });
  }

  async trackError(error, context = {}) {
    if (!this.userId) return;

    await this.trackAction('error', {
      error: error?.message || String(error),
      stack: error?.stack,
      ...context,
    });

    await this.updateRiskScore(['error_occurred']);
  }

  async updateRiskScore(riskFactors = []) {
    if (!this.userId || riskFactors.length === 0) return;

    try {
      const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);
      const userAnalyticsDoc = await getDoc(userAnalyticsRef);

      if (!userAnalyticsDoc.exists()) return;

      const currentData = userAnalyticsDoc.data();
      const existingFactors = currentData.riskFactors || [];
      const newFactors = [...existingFactors, ...riskFactors].slice(-50);
      const riskScore = this.calculateRiskScore(newFactors);

      await updateDoc(userAnalyticsRef, {
        riskScore,
        riskFactors: newFactors,
        updatedAt: Timestamp.now(),
      });

      if (riskScore > 70) {
        await systemLogger.logSecurityEvent('high_risk_user', 'high', {
          riskScore,
          riskFactors: newFactors,
        });
      }
    } catch (error) {
      console.error('Failed to update risk score:', error);
    }
  }

  calculateRiskScore(riskFactors) {
    const factorWeights = {
      multiple_failed_logins: 15,
      suspicious_location: 20,
      error_occurred: 5,
      unusual_activity: 10,
      permission_violation: 25,
      malicious_request: 30,
    };

    let score = 0;
    const factorCounts = {};

    riskFactors.forEach((factor) => {
      factorCounts[factor] = (factorCounts[factor] || 0) + 1;
    });

    Object.entries(factorCounts).forEach(([factor, count]) => {
      const weight = factorWeights[factor] || 5;
      score += weight * Math.min(count, 3);
    });

    return Math.min(score, 100);
  }

  async trackSessionEnd() {
    if (!this.userId || !this.sessionSampled) return;
    if (!this.shouldSample(SAMPLE.sessionEnd)) return;

    const sessionDuration = Date.now() - this.sessionStartTime;

    try {
      const userAnalyticsRef = doc(db, 'userAnalytics', this.userId);
      const userAnalyticsDoc = await getDoc(userAnalyticsRef);

      if (userAnalyticsDoc.exists()) {
        const data = userAnalyticsDoc.data();
        const loginHistory = [...(data.loginHistory || [])];

        if (loginHistory.length > 0) {
          loginHistory[loginHistory.length - 1] = {
            ...loginHistory[loginHistory.length - 1],
            sessionDuration,
          };
          const totalDuration = loginHistory.reduce(
            (sum, session) => sum + (session.sessionDuration || 0),
            0
          );
          const averageSessionTime = totalDuration / loginHistory.length;

          await updateDoc(userAnalyticsRef, {
            loginHistory,
            'engagementMetrics.averageSessionTime': averageSessionTime,
            updatedAt: Timestamp.now(),
          });
        }
      }
    } catch (error) {
      console.error('Failed to track session end:', error);
    }
  }

  setupVisibilityTracking() {
    if (this._visibilityBound || typeof document === 'undefined') return;
    this._visibilityBound = true;
    // Local only — do not write page_hidden / page_visible
    document.addEventListener('visibilitychange', () => {
      /* intentionally no Firestore writes */
    });
  }

  setupBeforeUnloadTracking() {
    if (this._unloadBound || typeof window === 'undefined') return;
    this._unloadBound = true;
    window.addEventListener('beforeunload', () => {
      if (this.sessionSampled) {
        // Best-effort; browsers may cancel the promise
        this.trackSessionEnd();
      }
    });
  }

  getTimezoneBasedLocation() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const parts = timezone.split('/');

      if (parts.length >= 2) {
        const region = parts[0].replace('_', ' ');
        const city = parts[1].replace('_', ' ');
        const regionCountryMap = {
          America: 'United States',
          Europe: 'Europe',
          Asia: 'Asia',
          Africa: 'Africa',
          Australia: 'Australia',
          Pacific: 'Pacific Region',
          Atlantic: 'Atlantic Region',
          Indian: 'Indian Ocean Region',
          Antarctica: 'Antarctica',
        };
        const country = regionCountryMap[region] || 'Unknown Region';
        return `${city}, ${country}`;
      }

      return 'Unknown Location';
    } catch {
      return 'Unknown Location';
    }
  }

  getDeviceType() {
    const userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
    if (
      /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
        userAgent
      )
    ) {
      return 'mobile';
    }
    return 'desktop';
  }
}

const analyticsService = new AnalyticsService();

export default analyticsService;
